import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Search, Tag } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PROBLEM_STATUSES, SEVERITIES } from '@odr/shared';

import { PageHeader } from '../../components/AppShell';
import {
  Badge,
  Card,
  EmptyState,
  Input,
  ListRow,
  Select,
  SkeletonRows,
  StatusPill,
  problemStatusTone,
  severityTone,
} from '../../components/ui';
import { api, qs } from '../../lib/api';
import { formatDate, humanise, relativeTime } from '../../lib/format';

interface ProblemRow {
  id: string;
  tagNo: string;
  title: string;
  severity: string;
  status: string;
  detectedAt: string;
  dueDate: string | null;
  machineCode: string;
  machineName: string;
  lineName: string;
  componentName: string | null;
  detectedByName: string;
  assigneeName: string | null;
  safetyImpact: boolean;
  qualityImpact: boolean;
  isOverdue: boolean;
}

export function ProblemListPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get('search') ?? '');

  const filters = {
    status: params.get('status') ?? '',
    severity: params.get('severity') ?? '',
    machineId: params.get('machineId') ?? '',
    openOnly: params.get('openOnly') ?? '',
    overdueOnly: params.get('overdueOnly') ?? '',
    mine: params.get('mine') ?? '',
  };

  const { data, isLoading } = useQuery({
    queryKey: ['problems', { ...filters, search }],
    queryFn: () =>
      api.get<{ data: ProblemRow[]; total: number }>(
        `/api/problem-tags${qs({ ...filters, search, pageSize: 100 })}`,
      ),
  });

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <div>
      <PageHeader
        title="Problem tags"
        subtitle={data ? `${data.total} tag${data.total === 1 ? '' : 's'} match` : 'Abnormalities found on the line'}
      />

      <div className="space-y-4 p-4 lg:p-6">
        <Card className="p-3">
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <div className="relative">
              <Search
                aria-hidden
                className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-steel-400"
              />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Tag number or words"
                aria-label="Search problem tags"
                className="pl-9"
              />
            </div>
            <Select
              value={filters.status}
              onChange={(event) => setParam('status', event.target.value)}
              aria-label="Filter by status"
            >
              <option value="">Any status</option>
              {PROBLEM_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {humanise(value)}
                </option>
              ))}
            </Select>
            <Select
              value={filters.severity}
              onChange={(event) => setParam('severity', event.target.value)}
              aria-label="Filter by severity"
            >
              <option value="">Any severity</option>
              {SEVERITIES.map((value) => (
                <option key={value} value={value}>
                  {humanise(value)}
                </option>
              ))}
            </Select>
            <Select
              value={filters.openOnly ? 'open' : filters.overdueOnly ? 'overdue' : ''}
              onChange={(event) => {
                const next = new URLSearchParams(params);
                next.delete('openOnly');
                next.delete('overdueOnly');
                if (event.target.value === 'open') next.set('openOnly', '1');
                if (event.target.value === 'overdue') next.set('overdueOnly', '1');
                setParams(next, { replace: true });
              }}
              aria-label="Quick filter"
            >
              <option value="">Everything</option>
              <option value="open">Still open</option>
              <option value="overdue">Overdue</option>
            </Select>
          </div>
        </Card>

        <Card>
          {isLoading ? (
            <SkeletonRows rows={6} />
          ) : !data?.data.length ? (
            <EmptyState
              icon={CheckCircle2}
              title="No open problem tags"
              message="Great — there are no unresolved problems matching these filters."
            />
          ) : (
            <ul className="divide-y divide-steel-100">
              {data.data.map((problem) => (
                <li key={problem.id}>
                  <ListRow onClick={() => navigate(`/problems/${problem.id}`)}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="flex flex-wrap items-center gap-x-2 text-sm font-bold text-steel-900">
                          <span className="font-mono text-info-700">{problem.tagNo}</span>
                          <span className="truncate">{problem.title}</span>
                        </p>
                        <p className="mt-0.5 truncate text-xs text-steel-500">
                          {problem.machineCode} · {problem.componentName ?? 'Machine level'} ·{' '}
                          {problem.lineName}
                        </p>
                        <p className="mt-0.5 text-xs text-steel-500">
                          Raised by {problem.detectedByName} {relativeTime(problem.detectedAt)}
                          {problem.assigneeName ? ` · assigned to ${problem.assigneeName}` : ''}
                        </p>
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {problem.safetyImpact ? <Badge tone="alarm">Safety</Badge> : null}
                          {problem.qualityImpact ? <Badge tone="warn">Quality</Badge> : null}
                          {problem.isOverdue ? (
                            <Badge tone="alarm">
                              Overdue since {formatDate(problem.dueDate)}
                            </Badge>
                          ) : null}
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <StatusPill
                          tone={severityTone(problem.severity)}
                          label={humanise(problem.severity)}
                        />
                        <StatusPill
                          tone={problemStatusTone(problem.status)}
                          label={humanise(problem.status)}
                        />
                      </div>
                    </div>
                  </ListRow>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

export { Tag };
